import React from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { useTranslations } from '../../hooks/useTranslations';
import { usePersistedPensieveLevelRange } from '../../hooks/usePersistedPensieveLevelRange';
import { getLanguage, type PensieveQuestionLevel } from '@deadpan/shared';

const LEVELS: PensieveQuestionLevel[] = [0, 1, 2, 3];

export const PensieveGame: React.FC = () => {
  const { socket, currentRoom, gameState, myPlayerId } = useSocket();
  const t = useTranslations();
  const { range, setMin, setMax } = usePersistedPensieveLevelRange();
  const isZh = getLanguage() === 'zh';

  if (!currentRoom || !gameState) {
    return null;
  }

  const operatorId = gameState.pensieveOperatorId;
  const respondentId = gameState.pensieveRespondentId;
  const question = gameState.pensieveCurrentQuestion;
  const pensieveMode = currentRoom.pensieveConfig?.memoryMode ?? gameState.pensieveMode ?? 'others';
  const isSelfMode = pensieveMode === 'self';
  const selfOrderIds = gameState.pensieveSelfOrder && gameState.pensieveSelfOrder.length > 0
    ? gameState.pensieveSelfOrder
    : (currentRoom.pensieveConfig?.selfDrawOrder ?? []);
  const isOperator = myPlayerId === operatorId;
  const isRespondent = isSelfMode ? isOperator : myPlayerId === respondentId;
  const respondentPlayer = currentRoom.players.find((p) => p.id === respondentId);
  const questionText = question
    ? (isZh ? question.questionZh : question.question)
    : '';

  const handleDraw = () => {
    if (!socket || !isOperator) return;
    socket.emit('client:pensieve-draw', range);
  };

  const handleNextRound = () => {
    if (!socket || !isRespondent) return;
    socket.emit('client:pensieve-next-round');
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 flex flex-col items-center">
      <div className="w-full text-center mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mb-2">
          {t.games.pensieve.title}
        </h2>
      </div>

      {isSelfMode && (
        <div className="w-full rounded-xl border-2 border-violet-200 bg-violet-50/80 p-4 mb-4">
          <p className="text-sm font-semibold text-violet-800 mb-3">{t.games.pensieve.turnOrderLabel}</p>
          <div className="flex flex-wrap justify-center gap-2">
            {selfOrderIds.map((playerId) => {
              const player = currentRoom.players.find((p) => p.id === playerId);
              if (!player) return null;
              const isCurrent = operatorId === playerId;
              return (
                <span
                  key={playerId}
                  className={`px-3 py-1 rounded-full border text-sm font-semibold ${
                    isCurrent
                      ? 'bg-violet-600 text-white border-violet-700 shadow'
                      : 'bg-white text-violet-700 border-violet-200'
                  }`}
                >
                  {player.name}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Operator panel */}
      {isOperator && (
        <div className="w-full rounded-xl border-2 border-purple-200 bg-purple-50/80 p-4 mb-4">
          <p className="text-sm font-semibold text-purple-800 mb-3">
            {isSelfMode ? t.games.pensieve.youAreCurrentPlayer : t.games.pensieve.youAreOperator}
          </p>
          <div className="flex flex-col gap-3 mb-3">
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center items-stretch gap-3">
              <div className="flex flex-wrap items-center gap-2 flex-1">
                <label htmlFor="pensieve-game-level-min" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  {t.games.pensieve.levelMinLabel}
                </label>
                <select
                  id="pensieve-game-level-min"
                  value={range.min}
                  onChange={(e) => setMin(Number(e.target.value) as PensieveQuestionLevel)}
                  className="w-full sm:w-auto min-w-[4rem] px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800"
                >
                  {LEVELS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap items-center gap-2 flex-1">
                <label htmlFor="pensieve-game-level-max" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  {t.games.pensieve.levelMaxLabel}
                </label>
                <select
                  id="pensieve-game-level-max"
                  value={range.max}
                  onChange={(e) => setMax(Number(e.target.value) as PensieveQuestionLevel)}
                  className="w-full sm:w-auto min-w-[4rem] px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-800"
                >
                  {LEVELS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* 他人模式：搅动在守护者区块；自己模式：搅动与「完成」同在下方琥珀区块 */}
            {!(isSelfMode && isRespondent) && (
              <div className="w-full">
                <button
                  type="button"
                  onClick={handleDraw}
                  className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-indigo-700"
                >
                  🔮 {t.games.pensieve.drawButton}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Current question & respondent (everyone sees) */}
      <div className="w-full rounded-xl border-2 border-indigo-200 bg-indigo-50/80 p-4 mb-4">
        <div className="text-sm font-semibold text-indigo-800 mb-2">{t.games.pensieve.questionLabel}</div>
        {question ? (
          <>
            <p className="text-sm text-indigo-600 mb-1">
              {t.games.pensieve.levelLabel}: {question.level}
            </p>
            <p className="text-lg sm:text-2xl font-semibold text-indigo-900 break-words mb-3 text-center">{questionText}</p>
            {isSelfMode ? null : (
              <div className="text-sm text-indigo-700">
                <span className="font-medium">{t.games.pensieve.respondentLabel}:</span> {respondentPlayer?.name ?? '—'}
              </div>
            )}
          </>
        ) : (
          <p className="text-gray-500">—</p>
        )}
      </div>

      {/* Respondent: next round button */}
      {isRespondent && question && (
        <div className="w-full rounded-xl border-2 border-amber-200 bg-amber-50/80 p-4 mb-4">
          <p className="text-sm font-semibold text-amber-800 mb-2">
            {isSelfMode ? t.games.pensieve.youCanCompleteTurn : t.games.pensieve.youAreRespondent}
          </p>
          <div className="flex flex-col gap-3">
            {isSelfMode && isOperator && (
              <button
                type="button"
                onClick={handleDraw}
                className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-indigo-700"
              >
                🔮 {t.games.pensieve.drawButton}
              </button>
            )}
            <button
              type="button"
              onClick={handleNextRound}
              className="w-full py-3 px-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600"
            >
              {isSelfMode ? t.games.pensieve.completeTurnButton : t.games.pensieve.nextRoundButton}
            </button>
          </div>
        </div>
      )}

      {isSelfMode && isRespondent && !question && (
        <div className="w-full rounded-xl border-2 border-amber-200 bg-amber-50/80 p-4 mb-4">
          <p className="text-sm font-semibold text-amber-800 mb-2">{t.games.pensieve.youCanCompleteTurn}</p>
          <div className="flex flex-col gap-3">
            {isOperator && (
              <button
                type="button"
                onClick={handleDraw}
                className="w-full py-3 px-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-indigo-700"
              >
                🔮 {t.games.pensieve.drawButton}
              </button>
            )}
            <button
              type="button"
              onClick={handleNextRound}
              className="w-full py-3 px-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:from-amber-600 hover:to-orange-600"
            >
              {t.games.pensieve.completeTurnButton}
            </button>
          </div>
        </div>
      )}

      {/* Observer hint */}
      {!isOperator && !isRespondent && question && (
        <p className="text-sm text-gray-600 text-center">
          {isSelfMode ? t.games.pensieve.waitingCurrentPlayer : t.games.pensieve.respondentWillStartNext}
        </p>
      )}
    </div>
  );
};
